import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Trash2, Pencil, ShieldCheck, Loader2, MapPin, CalendarX2, RefreshCw, ScrollText, X } from "lucide-react";
import { EditAnnonceModal } from "@/components/EditAnnonceModal";
import { CATEGORIES } from "@/lib/categories";
import { AIR_BENGUE } from "@/lib/airBengue";
import { formatDate } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIBELLES_STATUT = {
  active: "En ligne", outdated: "Désactivée (OutDate)", bloquee: "Bloquée",
  en_moderation: "À valider", refusee: "Refusée", expiree: "Expirée",
};
const LIBELLES_ACTION = {
  OUTDATE: "OutDate (désactivée)", RELANCER: "Relancée", BLOQUER: "Bloquée",
  DEBLOQUER: "Remise en ligne",
};
const horodatage = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Droits d'écriture de l'administrateur sur toutes les annonces du site. */
export const GestionAnnoncesAdmin = ({ adminId }) => {
  const [annonces, setAnnonces] = useState(null);
  const [statut, setStatut] = useState("");
  const [edition, setEdition] = useState(null);
  const [tarifs, setTarifs] = useState(null);
  const [historique, setHistorique] = useState(null);

  const charger = useCallback(() => {
    axios
      .get(`${API}/admin/annonces`, { params: { admin_id: adminId, statut: statut || undefined } })
      .then((r) => setAnnonces(r.data.annonces))
      .catch(() => setAnnonces([]));
  }, [adminId, statut]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    axios.get(`${API}/annonces/tarifs`).then((r) => setTarifs(r.data)).catch(() => {});
  }, []);

  const agir = async (annonce, decision) => {
    if (decision === "supprimer" && !window.confirm("Supprimer définitivement cette annonce ?")) return;
    let motif = "";
    if (decision === "bloquer") motif = window.prompt("Motif du blocage (visible par l'annonceur) :") || "";
    if (decision === "outdate") motif = window.prompt("Raison de la désactivation (optionnel) :") || "";
    if (decision === "relancer") motif = window.prompt("Raison de la relance (optionnel) :") || "";
    try {
      await axios.post(`${API}/admin/annonces/${annonce.id}/moderer`, null, {
        params: { admin_id: adminId, decision, motif: motif || undefined },
      });
      toast.success(
        decision === "bloquer" ? "Annonce bloquée"
          : decision === "debloquer" ? "Annonce remise en ligne"
          : decision === "outdate" ? "Annonce désactivée (OutDate)"
          : decision === "relancer" ? "Annonce relancée et prolongée"
          : "Annonce supprimée",
      );
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || "Action impossible.");
    }
  };

  return (
    <section className="mt-16" data-testid="gestion-annonces-admin">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E05638]">
            Droits d'écriture
          </p>
          <h2 className="font-heading mt-2 text-2xl font-bold text-[#12100E]">
            Gestion des annonces
          </h2>
        </div>
        <select
          value={statut}
          onChange={(e) => setStatut(e.target.value)}
          data-testid="admin-annonces-filtre-statut"
          className="rounded-full border border-[#E5DFD5] bg-white px-4 py-2 text-sm font-semibold text-stone-700"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(LIBELLES_STATUT).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {!annonces && (
        <p className="mt-6 flex items-center gap-2 text-sm text-stone-500">
          <Loader2 size={15} className="animate-spin" /> Chargement…
        </p>
      )}

      {annonces?.length === 0 && (
        <p className="mt-6 text-sm text-stone-500">Aucune annonce pour ce filtre.</p>
      )}

      <div className="mt-6 space-y-3">
        {annonces?.map((a) => {
          const cat = CATEGORIES[a.categorie] || AIR_BENGUE;
          return (
            <div
              key={a.id}
              data-testid={`admin-annonce-${a.id}`}
              className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#E5DFD5] bg-white p-4"
            >
              <span
                className="rounded-full px-3 py-1 text-[11px] font-bold uppercase text-white"
                style={{ backgroundColor: cat.color }}
              >
                {cat.name}
              </span>
              <div className="min-w-[200px] flex-1">
                <p className="text-sm font-bold text-stone-900">
                  {a.air?.titre || a.annonceur?.firstname}
                </p>
                <p className="mt-0.5 flex items-center gap-2 text-xs text-stone-500">
                  <MapPin size={11} /> {a.air?.ville || a.annonceur?.contact || "—"} ·{" "}
                  {formatDate(a.date_debut)} · {LIBELLES_STATUT[a.statut] || a.statut}
                </p>
                {a.motif_blocage && (
                  <p className="mt-1 text-xs font-semibold text-red-600">Motif : {a.motif_blocage}</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setEdition(a)}
                  data-testid={`admin-annonce-modifier-${a.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-3 py-1.5 text-xs font-bold text-stone-700 hover:border-stone-900"
                >
                  <Pencil size={13} /> Modifier
                </button>
                {a.statut === "bloquee" ? (
                  <button
                    onClick={() => agir(a, "debloquer")}
                    data-testid={`admin-annonce-debloquer-${a.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    <ShieldCheck size={13} /> Débloquer
                  </button>
                ) : (
                  <button
                    onClick={() => agir(a, "bloquer")}
                    data-testid={`admin-annonce-bloquer-${a.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#12100E] px-3 py-1.5 text-xs font-bold text-white"
                  >
                    <Ban size={13} /> Bloquer
                  </button>
                )}
                {a.statut === "active" ? (
                  <button
                    onClick={() => agir(a, "outdate")}
                    data-testid={`admin-annonce-outdate-${a.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    <CalendarX2 size={13} /> OutDate
                  </button>
                ) : (
                  <button
                    onClick={() => agir(a, "relancer")}
                    data-testid={`admin-annonce-relancer-${a.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0A5C36] px-3 py-1.5 text-xs font-bold text-white"
                  >
                    <RefreshCw size={13} /> Relancer
                  </button>
                )}
                <button
                  onClick={() => setHistorique(a)}
                  data-testid={`admin-annonce-historique-${a.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-3 py-1.5 text-xs font-bold text-stone-700 hover:border-stone-900"
                >
                  <ScrollText size={13} /> Historique
                  {a.historique?.length ? ` (${a.historique.length})` : ""}
                </button>
                <button
                  onClick={() => agir(a, "supprimer")}
                  data-testid={`admin-annonce-supprimer-${a.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={13} /> Effacer
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {historique && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
          onClick={() => setHistorique(null)}
          data-testid="admin-historique-modale"
        >
          <div
            className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-heading text-lg font-bold text-stone-900">
                Historique — {historique.air?.titre || historique.annonceur?.firstname}
              </h3>
              <button onClick={() => setHistorique(null)} data-testid="admin-historique-fermer" aria-label="Fermer">
                <X size={18} className="text-stone-500" />
              </button>
            </div>
            <div className="mt-5 space-y-3">
              {(historique.historique || []).length === 0 && (
                <p className="text-sm text-stone-500">Aucune action enregistrée sur cette annonce.</p>
              )}
              {[...(historique.historique || [])].reverse().map((h, i) => (
                <div
                  key={i}
                  data-testid={`admin-historique-ligne-${i}`}
                  className="rounded-2xl border-l-4 border-[#D4AF37] bg-[#FAF8F5] px-4 py-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-bold text-stone-900">
                      {LIBELLES_ACTION[h.action] || h.action}
                    </span>
                    <span className="text-xs text-stone-500">{horodatage(h.at)}</span>
                  </div>
                  <p className="mt-1 text-xs text-stone-600">Admin : {h.admin_email || h.admin_id}</p>
                  {h.motif && <p className="mt-0.5 text-xs text-stone-600">Raison : {h.motif}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {edition && tarifs && (
        <EditAnnonceModal
          annonce={edition}
          offre={tarifs[edition.categorie]?.[edition.palier] || { photosMax: 8, videoMax: 1 }}
          profileId={adminId}
          onClose={() => setEdition(null)}
          onSaved={charger}
        />
      )}
    </section>
  );
};
